import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Form, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { api } from '../../config/api';
import { useTheme } from '../../context/ThemeContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend
);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const money = (value) => `£${(parseFloat(value) || 0).toFixed(2)}`;

const splitLabel = (method) => {
  switch (method) {
    case 'FIFTY_FIFTY':
      return '50/50';
    case 'SALARY_PERCENTAGE':
      return 'Salary %';
    case 'FULL_RECLAIM':
      return 'Full reclaim';
    case 'NO_REIMBURSEMENT':
      return 'No reclaim';
    default:
      return method || '—';
  }
};

const CategoryAnalysis = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const currentYear = new Date().getFullYear();
  const year = parseInt(searchParams.get('year') || String(currentYear), 10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const isDark = theme === 'dark';
  const axisColor = isDark ? '#9aa3b2' : '#5a6270';
  const gridColor = isDark ? 'rgba(242,244,248,0.08)' : 'rgba(16,17,20,0.08)';
  const barColor = payload?.category?.color || (isDark ? '#6b8cff' : '#2155ff');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/statistics/categories/${id}`, {
          params: { year },
        });
        if (res.data.success) {
          setPayload(res.data.data);
        } else {
          setError(res.data.message || 'Failed to load category analysis.');
        }
      } catch (err) {
        console.error('Error loading category analysis:', err);
        setError(err.response?.data?.message || 'Failed to load category analysis.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, year]);

  const chartData = useMemo(() => {
    const monthly = payload?.monthly || [];
    return {
      labels: MONTHS,
      datasets: [
        {
          label: `${payload?.category?.name || 'Category'} spend`,
          data: MONTHS.map((_, index) => {
            const row = monthly.find((m) => Number(m.month) === index + 1);
            return row ? parseFloat(row.totalAmount || 0) : 0;
          }),
          backgroundColor: `${barColor}99`,
          borderColor: barColor,
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [payload, barColor]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => money(context.parsed.y),
          },
        },
      },
      scales: {
        x: {
          ticks: { color: axisColor },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: axisColor,
            callback: (value) => `£${value}`,
          },
          grid: { color: gridColor },
        },
      },
    }),
    [axisColor, gridColor]
  );

  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];
  const peakMonth = (payload?.monthly || []).reduce(
    (best, row) =>
      parseFloat(row.totalAmount || 0) > parseFloat(best.totalAmount || 0) ? row : best,
    { month: 1, totalAmount: 0 }
  );

  if (loading && !payload) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading analysis…</p>
      </div>
    );
  }

  if (error && !payload) {
    return (
      <Alert variant="danger" className="my-3">
        <Alert.Heading>Error</Alert.Heading>
        <p className="mb-3">{error}</p>
        <Link to="/analysis">Back to analysis</Link>
      </Alert>
    );
  }

  const totals = payload?.totals || {};
  const category = payload?.category || {};

  return (
    <div className="analysis-page">
      <div className="page-header">
        <div>
          <Link to={`/analysis`} className="small text-decoration-none d-inline-flex align-items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faArrowLeft} />
            All categories
          </Link>
          <p className="eyebrow">Category analysis</p>
          <h1 className="d-flex align-items-center gap-2">
            <span
              className="analysis-category-swatch"
              style={{ background: category.color, width: '0.85rem', height: '0.85rem' }}
              aria-hidden="true"
            />
            {category.name}
          </h1>
          <p className="text-muted mb-0">
            Spend for {year}. Splits were set when each expense was created.
          </p>
        </div>
        <Form.Select
          value={year}
          onChange={(e) => setSearchParams({ year: e.target.value })}
          style={{ width: '8rem' }}
          aria-label="Select year"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Form.Select>
      </div>

      {error && (
        <Alert variant="warning" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="balance-strip">
        <div className="summary-tile">
          <div className="label mb-2">{year} total</div>
          <div className="value">{money(totals.totalAmount)}</div>
          <div className="small text-muted mt-2">{totals.count || 0} expenses</div>
        </div>
        <div className="summary-tile">
          <div className="label mb-2">Monthly avg</div>
          <div className="value">{money(totals.monthlyAverage)}</div>
          <div className="small text-muted mt-2">Across active months</div>
        </div>
        <div className="summary-tile">
          <div className="label mb-2">Typical expense</div>
          <div className="value">{money(totals.avgAmount)}</div>
          <div className="small text-muted mt-2">
            Range {money(totals.minAmount)} – {money(totals.maxAmount)}
          </div>
        </div>
        <div className="summary-tile">
          <div className="label mb-2">Peak month</div>
          <div className="value">{MONTHS[(peakMonth.month || 1) - 1]}</div>
          <div className="small text-muted mt-2">{money(peakMonth.totalAmount)}</div>
        </div>
      </div>

      <section className="panel mb-4">
        <div className="panel-head">
          <h2 className="h5 mb-0">{year} monthly trend</h2>
        </div>
        <div className="panel-body">
          <div className="chart-frame">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-head d-flex justify-content-between align-items-center">
          <h2 className="h5 mb-0">Expenses in {year}</h2>
          <Link to="/expenses" className="small">
            Open expenses
          </Link>
        </div>
        <div className="panel-body pt-0">
          {(payload?.expenses || []).length === 0 ? (
            <p className="text-muted mb-0 py-3">No expenses in this category for {year}.</p>
          ) : (
            <>
              <div className="d-none d-md-block">
                <Table responsive hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Title</th>
                      <th>Paid by</th>
                      <th>Split</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payload.expenses.map((exp) => (
                      <tr key={exp.id}>
                        <td>{new Date(exp.date).toLocaleDateString('en-GB')}</td>
                        <td>
                          <Link to={`/expenses/${exp.id}`}>{exp.title}</Link>
                        </td>
                        <td>{exp.paidBy?.displayName || '—'}</td>
                        <td>{splitLabel(exp.splitMethod)}</td>
                        <td className="text-end money-figure">{money(exp.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <ul className="analysis-expense-list d-md-none mb-0">
                {payload.expenses.map((exp) => (
                  <li key={exp.id}>
                    <Link to={`/expenses/${exp.id}`} className="analysis-expense-card text-decoration-none">
                      <div className="d-flex justify-content-between gap-2">
                        <strong>{exp.title}</strong>
                        <span className="money-figure">{money(exp.amount)}</span>
                      </div>
                      <div className="small text-muted mt-1">
                        {new Date(exp.date).toLocaleDateString('en-GB')} ·{' '}
                        {exp.paidBy?.displayName || '—'} · {splitLabel(exp.splitMethod)}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
              {payload.expenses.length >= 100 && (
                <p className="small text-muted mt-3 mb-0">
                  Showing the latest 100 expenses for this year.
                </p>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default CategoryAnalysis;
